import { EventDispatcher } from '../EventDispatcher';
import { Viewer } from '../viewer/viewer';
import { IPersona, ToolEvents } from './types';
import { ToolUndoRedoManager } from './tools/ToolUndoRedoManager';
import { ISubTool, SubToolInputKind, ALL_SUBTOOL_INPUT } from './SubTool';
import type { BasePlaneState } from './tools/implementations/line-tool/SelectionContextProvider';

/**
 * Base class for hosted interaction units (subtools). Mirrors the slice of
 * `BaseTool` that a subtool actually needs — viewer/persona, opt-in undo/redo,
 * and event dispatching — WITHOUT the `ITool` lifecycle and DOM listener
 * binding. A subtool never touches the DOM: its host owns input and forwards
 * already-drag-detected events, and the host owns drag detection, Ctrl+Z
 * routing, and toolbar registration.
 *
 * Two clean hierarchies result: `BaseTool` for top-level DOM-owning tools
 * (including hosts), `SubToolBase` for hosted units. `LineTool` extends this.
 */
export abstract class SubToolBase extends EventDispatcher implements ISubTool {
  protected viewer: Viewer;
  protected persona: IPersona;

  abstract readonly name: string;

  private _undoRedo: ToolUndoRedoManager | null = null;
  private _active = false;

  constructor(viewer: Viewer, persona: IPersona) {
    super();
    this.viewer = viewer;
    this.persona = persona;
  }

  // ─── Lifecycle (host-driven) ────────────────────────────────────────

  activate(): void {
    if (this._active) return;
    this._active = true;
    this.onActivate();
  }

  deactivate(): void {
    if (!this._active) return;
    this._active = false;
    this.onDeactivate();
    // Undo history is per-activation, like BaseTool clears it on disable.
    if (this._undoRedo) {
      this._undoRedo.clear();
      this.dispatchUndoRedoChanged();
    }
    // Mirror BaseTool.onDisable: tell the React HintBar this tool went away so
    // the quest tooltip clears. Without this, a subtool's phase hint (e.g. the
    // inner LineTool's "brush inside an area" during door/window wall focus)
    // stays on screen after the subtool is torn down — `tool_state_changed`
    // set the hint, but only `tool_disabled` hides it.
    this.dispatchEvent({ type: ToolEvents.DISABLED, tool: this });
    this.viewer.scene.dispatchEvent({ type: ToolEvents.DISABLED, tool: this });
  }

  protected get active(): boolean {
    return this._active;
  }

  /** Hook: subtool came live. Override for setup (overlays, mode activation). */
  protected onActivate(): void {
    // Subclasses override
  }

  /** Hook: subtool went idle. Override for teardown. */
  protected onDeactivate(): void {
    // Subclasses override
  }

  // ─── ISubTool defaults ──────────────────────────────────────────────

  /** Default: accept all input kinds. Override to narrow. */
  getInputMask(): ReadonlySet<SubToolInputKind> {
    return ALL_SUBTOOL_INPUT;
  }

  /** Subclasses that draw on a plane override this. */
  setBasePlane(_state: BasePlaneState | null): void {
    // Subclasses override
  }

  dispose(): void {
    this.deactivate();
  }

  // ─── Undo/Redo (host triggers; subtool owns the stack) ──────────────

  /** Initialize undo/redo support. Call from the subtool constructor. */
  protected initUndoRedo(): ToolUndoRedoManager {
    const mgr = new ToolUndoRedoManager();
    mgr.onStackChanged = () => this.dispatchUndoRedoChanged();
    this._undoRedo = mgr;
    return mgr;
  }

  /** The undo/redo manager (if initialized). */
  get undoRedo(): ToolUndoRedoManager | null {
    return this._undoRedo;
  }

  /** Perform undo. Hosts route Ctrl+Z here when this subtool should own it. */
  performUndo(): boolean {
    return this._undoRedo?.undo() ?? false;
  }

  /** Perform redo. */
  performRedo(): boolean {
    return this._undoRedo?.redo() ?? false;
  }

  protected dispatchUndoRedoChanged(): void {
    if (!this._undoRedo) return;
    const event = {
      type: ToolEvents.UNDO_REDO_CHANGED,
      tool: this,
      canUndo: this._undoRedo.canUndo,
      canRedo: this._undoRedo.canRedo,
    };
    this.dispatchEvent(event);
    this.viewer.scene.dispatchEvent(event);
  }

  // ─── State dispatch (quest/tooltip system) ──────────────────────────

  protected dispatchStateChanged(state: { phase: string }): void {
    // Late async work (aborted detections, settled worker promises) may try to
    // dispatch a phase hint after deactivate() already dispatched DISABLED —
    // the HintBar only hides on DISABLED, so a straggler STATE_CHANGED would
    // resurrect the tooltip and leave it stuck on screen.
    if (!this._active) return;
    this.dispatchEvent({ type: ToolEvents.STATE_CHANGED, tool: this, state });
    this.viewer.scene.dispatchEvent({ type: ToolEvents.STATE_CHANGED, tool: this, state });
  }
}
